import { api, hasBackend } from "./api";
import { upcomingPayments, recentPayments } from "@/mocks/payments";
import type { Payment, PaymentCreate, QuickStats } from "@/types";

export async function fetchPayments(): Promise<Payment[]> {
  if (!hasBackend()) return [...upcomingPayments, ...recentPayments] as Payment[];
  return api.get<Payment[]>("/api/v1/payments");
}

export async function fetchPayment(id: string): Promise<Payment | null> {
  if (!hasBackend()) {
    return ([...upcomingPayments, ...recentPayments] as Payment[]).find((p) => p.id === id) ?? null;
  }
  return api.get<Payment>(`/api/v1/payments/${id}`);
}

export async function createPayment(data: PaymentCreate): Promise<Payment> {
  if (!hasBackend()) {
    const mock: Payment = {
      id: String(Date.now()),
      ...data,
      status: "pending",
      icon: data.icon ?? "ri-bill-line",
      color: data.color ?? "#6366f1",
    };
    upcomingPayments.push(mock as never);
    return mock;
  }
  return api.post<Payment>("/api/v1/payments", data);
}

export async function updatePayment(id: string, data: Partial<PaymentCreate>): Promise<Payment> {
  if (!hasBackend()) {
    const all = [...upcomingPayments, ...recentPayments] as Payment[];
    const p = all.find((x) => x.id === id);
    if (!p) throw new Error("Payment not found");
    return { ...p, ...data };
  }
  return api.patch<Payment>(`/api/v1/payments/${id}`, data);
}

export async function markPaymentPaid(id: string): Promise<Payment> {
  if (!hasBackend()) {
    const all = [...upcomingPayments, ...recentPayments] as Payment[];
    const p = all.find((x) => x.id === id);
    if (!p) throw new Error("Payment not found");
    return { ...p, status: "paid" };
  }
  return api.patch<Payment>(`/api/v1/payments/${id}/status`, { status: "paid" });
}

export async function deletePayment(id: string): Promise<void> {
  if (!hasBackend()) return;
  return api.delete<void>(`/api/v1/payments/${id}`);
}

export function computeQuickStats(payments: Payment[], tillDate?: string): QuickStats {
  const today = new Date();
  const cutoff = tillDate ? new Date(tillDate) : null;
  const monthPayments = payments.filter((p) => {
    const d = new Date(p.dueDate);
    return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  });
  const unpaidMonth = monthPayments.filter((p) => p.status !== "paid");
  const filtered = cutoff ? unpaidMonth.filter((p) => new Date(p.dueDate) <= cutoff) : unpaidMonth;
  const pendingAll = payments.filter((p) => p.status === "pending");
  const filteredPending = cutoff ? pendingAll.filter((p) => new Date(p.dueDate) <= cutoff) : pendingAll;
  return {
    totalDueThisMonth: filtered.reduce((s, p) => s + p.amount, 0),
    upcomingCount: filteredPending.length,
    paidThisMonth: monthPayments.filter((p) => p.status === "paid").length,
    overdueCount: payments.filter((p) => p.status === "overdue").length,
    monthlyBudget: 3500,
  };
}
